import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, hasAdminAccess, hasUserAccess, hasAcceptedTerms } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/loginPage" replace />;
  }

  // Check if user has accepted terms (skip check for /terms route to avoid redirect loop)
  if (location.pathname !== '/terms' && !hasAcceptedTerms()) {
    return <Navigate to="/terms" replace />;
  }

  if (!hasAdminAccess()) {
    // If user has USER_ACCESS but not ADMIN_ACCESS, redirect to user portal
    if (hasUserAccess()) {
      return <Navigate to="/user" replace />;
    }
    
    // If user has no permissions, show access denied
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <strong>Access Denied</strong>
          <br />
          You don't have permission to access the admin portal. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};

export default AdminRoute; 